import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { LanguageCode, LANGUAGES_LIST } from '@/data/languages';
import { Globe, Plus, Sparkles, PenLine, Loader2, Trash2, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useLanguage, TranslationMode } from '../context/LanguageContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// Common languages to show first
const COMMON_LANGUAGES: LanguageCode[] = ['en', 'fr', 'es', 'de', 'it', 'pt', 'nl', 'pl', 'ru', 'ja', 'zh'];

// Sort languages: common first, then others
const sortedLanguages = [
  ...LANGUAGES_LIST.filter((lang) => COMMON_LANGUAGES.includes(lang.code as LanguageCode)).sort(
    (a, b) =>
      COMMON_LANGUAGES.indexOf(a.code as LanguageCode) - COMMON_LANGUAGES.indexOf(b.code as LanguageCode),
  ),
  ...LANGUAGES_LIST.filter((lang) => !COMMON_LANGUAGES.includes(lang.code as LanguageCode)).sort((a, b) =>
    a.name.localeCompare(b.name),
  ),
];

// Special value to trigger add language dialog
const ADD_LANGUAGE_VALUE = '__add_language__';

type DialogStep = 'select-language' | 'configure-translation';

export function LanguageSelector() {
  const { t } = useTranslation('storefront');
  const {
    selectedLanguage,
    setSelectedLanguage,
    availableLanguages,
    addLanguageWithTranslation,
    removeLanguage,
    isTranslating,
    isRemoving,
  } = useLanguage();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [dialogStep, setDialogStep] = useState<DialogStep>('select-language');
  const [searchValue, setSearchValue] = useState('');
  const [targetLanguage, setTargetLanguage] = useState<LanguageCode | null>(null);
  const [sourceLanguage, setSourceLanguage] = useState<LanguageCode>(
    availableLanguages.includes('en' as LanguageCode) ? 'en' as LanguageCode : availableLanguages[0] || 'en'
  );
  const [translationMode, setTranslationMode] = useState<TranslationMode>('ai');
  const [languageToRemove, setLanguageToRemove] = useState<LanguageCode | null>(null);

  const currentLang = LANGUAGES_LIST.find((l) => l.code === selectedLanguage);

  // Filter to only show available languages
  const availableLanguagesList = LANGUAGES_LIST.filter((lang) =>
    availableLanguages.includes(lang.code as LanguageCode),
  );

  // Sort available languages: maintain priority order
  const sortedAvailableLanguages = [
    ...availableLanguagesList
      .filter((lang) => COMMON_LANGUAGES.includes(lang.code as LanguageCode))
      .sort(
        (a, b) =>
          COMMON_LANGUAGES.indexOf(a.code as LanguageCode) - COMMON_LANGUAGES.indexOf(b.code as LanguageCode),
      ),
    ...availableLanguagesList
      .filter((lang) => !COMMON_LANGUAGES.includes(lang.code as LanguageCode))
      .sort((a, b) => a.name.localeCompare(b.name)),
  ];

  // Languages that can be added (not already available)
  const addableLanguages = sortedLanguages.filter(
    (lang) => !availableLanguages.includes(lang.code as LanguageCode),
  );

  const handleValueChange = (value: string) => {
    if (value === ADD_LANGUAGE_VALUE) {
      setDialogStep('select-language');
      setTargetLanguage(null);
      setSourceLanguage(availableLanguages.includes('en' as LanguageCode) ? 'en' as LanguageCode : availableLanguages[0] || 'en');
      setTranslationMode('ai');
      setShowAddDialog(true);
    } else {
      setSelectedLanguage(value as LanguageCode);
    }
  };

  const handleSelectTargetLanguage = (langCode: string) => {
    setTargetLanguage(langCode as LanguageCode);
    setSearchValue('');
    setDialogStep('configure-translation');
  };

  const handleAddLanguage = async () => {
    if (!targetLanguage) return;

    await addLanguageWithTranslation({
      targetLanguage,
      sourceLanguage,
      translationMode,
    });

    setShowAddDialog(false);
    setDialogStep('select-language');
    setTargetLanguage(null);
    setSearchValue('');
  };

  const handleDialogClose = (open: boolean) => {
    if (!open && !isTranslating) {
      setShowAddDialog(false);
      setDialogStep('select-language');
      setTargetLanguage(null);
      setSearchValue('');
    }
  };

  const targetLangInfo = targetLanguage ? LANGUAGES_LIST.find((l) => l.code === targetLanguage) : null;
  const sourceLangInfo = LANGUAGES_LIST.find((l) => l.code === sourceLanguage);
  const removeLanguageInfo = languageToRemove ? LANGUAGES_LIST.find((l) => l.code === languageToRemove) : null;

  const handleRemoveLanguage = async () => {
    if (!languageToRemove) return;
    await removeLanguage(languageToRemove);
    setLanguageToRemove(null);
  };

  const canRemoveLanguages = availableLanguages.length > 1;

  return (
    <>
      <div className="flex items-center gap-2">
        <Globe className="text-muted-foreground h-4 w-4" />
        <Select value={selectedLanguage} onValueChange={handleValueChange}>
          <SelectTrigger className="h-9 w-[220px]">
            <SelectValue>
              {currentLang && (
                <div className="flex items-center gap-2">
                  <img
                    src={`https://flagcdn.com/w20/${currentLang.flagCode}.png`}
                    alt={currentLang.flagCode}
                    className="h-3 w-4 shrink-0 object-cover"
                  />
                  <span className="text-sm">{currentLang.name}</span>
                  <span className="text-muted-foreground text-xs">({currentLang.code.toUpperCase()})</span>
                </div>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel className="text-muted-foreground text-xs font-medium">
                Available Languages ({sortedAvailableLanguages.length})
              </SelectLabel>
              {sortedAvailableLanguages.map((lang) => (
                <div key={lang.code} className="relative">
                  <SelectItem value={lang.code} className={canRemoveLanguages ? 'pr-10' : ''}>
                    <div className="flex items-center gap-2">
                      <img
                        src={`https://flagcdn.com/w20/${lang.flagCode}.png`}
                        alt={lang.flagCode}
                        className="h-3 w-4 shrink-0 object-cover"
                      />
                      <span className="text-sm">{lang.name}</span>
                      <span className="text-muted-foreground text-xs">({lang.code.toUpperCase()})</span>
                    </div>
                  </SelectItem>
                  {canRemoveLanguages && (
                    <button
                      type="button"
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setLanguageToRemove(lang.code as LanguageCode);
                      }}
                      className="text-muted-foreground hover:text-destructive absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded p-1 transition-colors hover:bg-red-50"
                      title={`Remove ${lang.name}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </SelectGroup>
            {addableLanguages.length > 0 && (
              <>
                <SelectSeparator />
                <SelectItem value={ADD_LANGUAGE_VALUE} className="text-primary">
                  <div className="flex items-center gap-2">
                    <Plus className="h-4 w-4" />
                    <span className="text-sm font-medium">{t('configEditor.language.addLanguageAction')}</span>
                  </div>
                </SelectItem>
              </>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Add Language Dialog */}
      <Dialog open={showAddDialog} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-md">
          {dialogStep === 'select-language' ? (
            <>
              <DialogHeader>
                <DialogTitle>{t('configEditor.language.addLanguage')}</DialogTitle>
                <DialogDescription>{t('configEditor.language.addLanguageDesc')}</DialogDescription>
              </DialogHeader>
              <Command className="border-border rounded-lg border">
                <CommandInput
                  placeholder={t('configEditor.language.searchLanguages')}
                  value={searchValue}
                  onValueChange={setSearchValue}
                />
                <CommandList className="max-h-[300px]">
                  <CommandEmpty>{t('configEditor.language.noLanguageFound')}</CommandEmpty>
                  <CommandGroup>
                    {addableLanguages.map((lang) => (
                      <CommandItem
                        key={lang.code}
                        value={`${lang.name} ${lang.code}`}
                        onSelect={() => handleSelectTargetLanguage(lang.code)}
                        className="cursor-pointer"
                      >
                        <div className="flex w-full items-center gap-2">
                          <img
                            src={`https://flagcdn.com/w20/${lang.flagCode}.png`}
                            alt={lang.flagCode}
                            className="h-3 w-4 shrink-0 object-cover"
                          />
                          <span className="text-sm">{lang.name}</span>
                          <span className="text-muted-foreground text-xs">({lang.code.toUpperCase()})</span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  Add{' '}
                  {targetLangInfo && (
                    <span className="inline-flex items-center gap-1.5">
                      <img
                        src={`https://flagcdn.com/w20/${targetLangInfo.flagCode}.png`}
                        alt={targetLangInfo.flagCode}
                        className="h-3 w-4 shrink-0 object-cover"
                      />
                      {targetLangInfo.name}
                    </span>
                  )}
                </DialogTitle>
                <DialogDescription>
                  Configure how you want to initialize translations for this language.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                {/* Source Language */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t('configEditor.language.sourceLanguage')}</Label>
                  <p className="text-muted-foreground text-xs">
                    Select the language to translate from (content will be copied or translated from this language)
                  </p>
                  <Select value={sourceLanguage} onValueChange={(v) => setSourceLanguage(v as LanguageCode)} disabled={isTranslating}>
                    <SelectTrigger className="w-full">
                      <SelectValue>
                        {sourceLangInfo && (
                          <div className="flex items-center gap-2">
                            <img
                              src={`https://flagcdn.com/w20/${sourceLangInfo.flagCode}.png`}
                              alt={sourceLangInfo.flagCode}
                              className="h-3 w-4 shrink-0 object-cover"
                            />
                            <span className="text-sm">{sourceLangInfo.name}</span>
                          </div>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {sortedAvailableLanguages.map((lang) => (
                        <SelectItem key={lang.code} value={lang.code}>
                          <div className="flex items-center gap-2">
                            <img
                              src={`https://flagcdn.com/w20/${lang.flagCode}.png`}
                              alt={lang.flagCode}
                              className="h-3 w-4 shrink-0 object-cover"
                            />
                            <span className="text-sm">{lang.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Translation Mode */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t('configEditor.language.translationMethod')}</Label>
                  <RadioGroup
                    value={translationMode}
                    onValueChange={(v) => setTranslationMode(v as TranslationMode)}
                    className="grid gap-2"
                    disabled={isTranslating}
                  >
                    <label
                      htmlFor="ai"
                      className={`border-border hover:bg-accent flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                        translationMode === 'ai' ? 'border-primary bg-primary/5' : ''
                      }`}
                    >
                      <RadioGroupItem value="ai" id="ai" className="mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Sparkles className="text-primary h-4 w-4" />
                          <span className="text-sm font-medium">{t('configEditor.language.aiTranslation')}</span>
                        </div>
                        <p className="text-muted-foreground mt-1 text-xs">
                          Automatically translate all text content using AI. Fast and accurate for most use cases.
                        </p>
                      </div>
                    </label>

                    <label
                      htmlFor="manual"
                      className={`border-border hover:bg-accent flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                        translationMode === 'manual' ? 'border-primary bg-primary/5' : ''
                      }`}
                    >
                      <RadioGroupItem value="manual" id="manual" className="mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <PenLine className="h-4 w-4" />
                          <span className="text-sm font-medium">{t('configEditor.language.manualTranslation')}</span>
                        </div>
                        <p className="text-muted-foreground mt-1 text-xs">
                          Copy content from source language. You&apos;ll translate the text yourself.
                        </p>
                      </div>
                    </label>
                  </RadioGroup>
                </div>
              </div>

              {isTranslating && (
                <Alert variant="warning">
                  <Clock className="h-4 w-4" />
                  <AlertDescription>
                    Translation in progress. This may take up to 2 minutes. Please do not close this window.
                  </AlertDescription>
                </Alert>
              )}

              <DialogFooter className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDialogStep('select-language')}
                  disabled={isTranslating}
                >
                  {t('configEditor.language.back')}
                </Button>
                <Button onClick={handleAddLanguage} disabled={isTranslating}>
                  {isTranslating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('configEditor.language.translating')}
                    </>
                  ) : translationMode === 'ai' ? (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      {t('configEditor.language.addAndTranslate')}
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      {t('configEditor.language.addLanguage')}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove Language Confirmation Dialog */}
      <AlertDialog open={!!languageToRemove} onOpenChange={(open) => {
        if (!open) setLanguageToRemove(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              Remove{' '}
              {removeLanguageInfo && (
                <span className="inline-flex items-center gap-1.5">
                  <img
                    src={`https://flagcdn.com/w20/${removeLanguageInfo.flagCode}.png`}
                    alt={removeLanguageInfo.flagCode}
                    className="h-3 w-4 shrink-0 object-cover"
                  />
                  {removeLanguageInfo.name}
                </span>
              )}
              ?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove all {removeLanguageInfo?.name || languageToRemove} translations from your
              configuration. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>{t('configEditor.language.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveLanguage}
              disabled={isRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemoving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('configEditor.language.removing')}
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('configEditor.language.removeLanguage')}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
